import { useState, useEffect } from 'react';
import { ChevronLeft, Edit, User, Calendar, FileText, Truck, UserCheck, X, Download, Eye } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { SignedLink, SignedImage } from '../common/SignedFile';
import { downloadDocument } from '../../utils/downloadDocument';
import { formatChicagoDate, formatChicagoDateTime } from '../../utils/dateFormatter';
// import { DriverSMSThreads } from './DriverSMSThreads'; // SMS removed per user request

interface DriverDetailPageProps {
  driverId: string;
}

export function DriverDetailPage({ driverId }: DriverDetailPageProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [driver, setDriver] = useState<any>(null);
  const [truck, setTruck] = useState<any>(null);
  const [dispatcher, setDispatcher] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showTruckModal, setShowTruckModal] = useState(false);
  const [showDispatcherModal, setShowDispatcherModal] = useState(false);
  const [availableTrucks, setAvailableTrucks] = useState<any[]>([]);
  const [dispatchers, setDispatchers] = useState<any[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);

  async function handleDownloadTerminationLetter() {
    try {
      await downloadDocument(driver.termination_letter, `${driver.first_name}_${driver.last_name}_Termination_Letter`);
    } catch {
      toast.error('Failed to download termination letter');
    }
  }

  useEffect(() => {
    loadDriverData();
  }, [driverId]);

  async function loadDriverData() {
    try {
      // Load driver data by ID
      const { data: driverData, error: driverError} = await supabase
        .from('drivers')
        .select('*')
        .eq('id', driverId)
        .single();

      if (driverError) throw driverError;
      
      // Load company data separately if driver has a company
      if (driverData.company_id) {
        const { data: companyData } = await supabase
          .from('companies')
          .select('id, name, code')
          .eq('id', driverData.company_id)
          .single();
        
        if (companyData) {
          driverData.companies = companyData;
        }
      }
      
      setDriver(driverData);

      // Load assigned truck from equipment_assignments. Ordered + limited
      // rather than a bare .maybeSingle(): a driver carrying more than one
      // is_current row made maybeSingle() error and the truck silently vanish.
      const { data: assignment, error: assignmentError } = await supabase
        .from('equipment_assignments')
        .select('truck_id')
        .eq('driver_id', driverData.id)
        .eq('is_current', true)
        .order('assigned_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (!assignmentError && assignment?.truck_id) {
        const { data: truckData } = await supabase
          .from('trucks')
          .select('id, unit_number, make, model, year, vin')
          .eq('id', assignment.truck_id)
          .single();
        
        setTruck(truckData || null);
      } else {
        // No current assignment — clear any previously shown truck, otherwise
        // an unassign leaves the old truck on screen.
        setTruck(null);
      }

      // Load dispatcher if driver has one
      if (driverData.dispatcher_id) {
        const { data: dispatcherData } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .eq('id', driverData.dispatcher_id)
          .single();
        
        setDispatcher(dispatcherData);
      } else {
        setDispatcher(null);
      }

      // Load available trucks and dispatchers for assignment
      const { data: assignedTrucks, error: assignedError } = await supabase
        .from('equipment_assignments')
        .select('truck_id')
        .eq('is_current', true);
      
      if (assignedError) {
        console.warn('Could not load equipment assignments:', assignedError.message);
      }
      
      const assignedTruckIds = new Set(assignedTrucks?.map(a => a.truck_id) || []);
      
      const { data: allTrucks } = await supabase
        .from('trucks')
        .select('id, unit_number, make, model, year')
        .eq('status', 'in_service')
        .order('unit_number');
      
      setAvailableTrucks(allTrucks?.filter(t => !assignedTruckIds.has(t.id)) || []);

      const { data: allDispatchers } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('role', 'Dispatch')
        .order('full_name');
      
      setDispatchers(allDispatchers || []);
    } catch (error) {
      console.error('Error loading driver:', error);
      toast.error('Failed to load driver data');
    } finally {
      setLoading(false);
    }
  }

  const handleAssignTruck = async (truckId: string) => {
    if (!driver) return;
    setAssigning(true);
    try {
      const now = new Date().toISOString();

      // Release the driver's existing assignment before creating the new one.
      // Without this the driver accumulates multiple is_current rows and every
      // screen that resolves "current truck" picks between them arbitrarily.
      const { error: releaseDriverError } = await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: now })
        .eq('driver_id', driver.id)
        .eq('is_current', true);

      if (releaseDriverError) throw releaseDriverError;

      // Release whoever currently holds the target truck, so one truck is
      // never current for two drivers at once.
      const { error: releaseTruckError } = await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: now })
        .eq('truck_id', truckId)
        .eq('is_current', true);

      if (releaseTruckError) throw releaseTruckError;

      const { error } = await supabase
        .from('equipment_assignments')
        .insert({
          driver_id: driver.id,
          truck_id: truckId,
          is_current: true,
          assigned_at: now
        });

      if (error) throw error;
      toast.success('Truck assigned successfully!');
      setShowTruckModal(false);
      loadDriverData();
    } catch (error) {
      console.error('Error assigning truck:', error);
      toast.error('Failed to assign truck');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignTruck = async () => {
    if (!driver || !truck) return;
    if (!confirm('Are you sure you want to unassign this truck?')) return;
    
    setAssigning(true);
    try {
      const { error } = await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: new Date().toISOString() })
        .eq('driver_id', driver.id)
        .eq('truck_id', truck.id)
        .eq('is_current', true);

      if (error) throw error;
      toast.success('Truck unassigned successfully!');
      loadDriverData();
    } catch (error) {
      console.error('Error unassigning truck:', error);
      toast.error('Failed to unassign truck');
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignDispatcher = async (dispatcherId: string) => {
    if (!driver) return;
    setAssigning(true);
    try {
      const { error } = await supabase
        .from('drivers')
        .update({ dispatcher_id: dispatcherId || null })
        .eq('id', driver.id);

      if (error) throw error;
      toast.success(dispatcherId ? 'Dispatcher assigned successfully!' : 'Dispatcher unassigned successfully!');
      setShowDispatcherModal(false);
      loadDriverData();
    } catch (error) {
      console.error('Error assigning dispatcher:', error);
      toast.error('Failed to assign dispatcher');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Driver not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/drivers')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">{driver.first_name} {driver.last_name}</h1>
                  <p className="text-sm text-slate-600">Driver Details</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate(`/drivers/edit/${driver.id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <Edit className="w-4 h-4" />
              Edit Driver
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="columns-1 lg:columns-2 xl:columns-3 gap-6 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 break-inside-avoid mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Personal Information</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-600">Name</label>
                <p className="text-slate-900 font-semibold">{driver.first_name} {driver.last_name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Company</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  driver.companies?.code === 'APOLLO' ? 'bg-blue-100 text-blue-800' :
                  driver.companies?.code === 'TESLA' ? 'bg-purple-100 text-purple-800' :
                  driver.companies?.code === 'TMARK' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {driver.companies?.name || 'Unassigned'}
                </span>
              </div>
              {driver.driver_type && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Driver Type</label>
                  <p className="text-slate-900 capitalize">{driver.driver_type.replace('_', ' ')}</p>
                </div>
              )}
              {driver.date_of_birth && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Date of Birth</label>
                  <p className="text-slate-900">{formatChicagoDate(driver.date_of_birth)}</p>
                </div>
              )}
              {driver.years_of_experience && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Years of Experience</label>
                  <p className="text-slate-900">{driver.years_of_experience} years</p>
                </div>
              )}
              {driver.phone_number && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Phone Number</label>
                  <p className="text-slate-900">{driver.phone_number}</p>
                </div>
              )}
              {driver.fuel_card_number && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Fuel Card Number</label>
                  <p className="text-slate-900 font-mono">{driver.fuel_card_number}</p>
                </div>
              )}
              {driver.ssn && (
                <div>
                  <label className="text-sm font-medium text-slate-600">SSN</label>
                  <p className="text-slate-900 font-mono">{driver.ssn}</p>
                </div>
              )}
              {driver.address && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Address</label>
                  <p className="text-slate-900">{driver.address}</p>
                </div>
              )}
              {driver.city && (
                <div>
                  <label className="text-sm font-medium text-slate-600">City</label>
                  <p className="text-slate-900">{driver.city}</p>
                </div>
              )}
              {driver.state && (
                <div>
                  <label className="text-sm font-medium text-slate-600">State</label>
                  <p className="text-slate-900">{driver.state}</p>
                </div>
              )}
              {driver.zip_code && (
                <div>
                  <label className="text-sm font-medium text-slate-600">ZIP Code</label>
                  <p className="text-slate-900">{driver.zip_code}</p>
                </div>
              )}
            </div>
          </div>

          {/* CDL Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 break-inside-avoid mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              CDL Information
            </h2>
            <div className="space-y-3">
              {driver.cdl_number && (
                <div>
                  <label className="text-sm font-medium text-slate-600">CDL Number</label>
                  <p className="text-slate-900">{driver.cdl_number}</p>
                </div>
              )}
              {driver.cdl_state && (
                <div>
                  <label className="text-sm font-medium text-slate-600">CDL State</label>
                  <p className="text-slate-900">{driver.cdl_state}</p>
                </div>
              )}
              {driver.cdl_expiration && (
                <div>
                  <label className="text-sm font-medium text-slate-600">CDL Expiration</label>
                  <p className="text-slate-900">{formatChicagoDate(driver.cdl_expiration)}</p>
                </div>
              )}
            </div>
          </div>

          {/* MVR & Medical */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 break-inside-avoid mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">MVR & Medical</h2>
            <div className="space-y-3">
              {driver.mvr_notes && (
                <div>
                  <label className="text-sm font-medium text-slate-600">MVR Notes</label>
                  <p className="text-slate-900">{driver.mvr_notes}</p>
                </div>
              )}
              {driver.mvr_status && (
                <div>
                  <label className="text-sm font-medium text-slate-600">MVR Status</label>
                  <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                    driver.mvr_status === 'CLEAN' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {driver.mvr_status}
                  </span>
                </div>
              )}
              {driver.mvr_expiration_date && (
                <div>
                  <label className="text-sm font-medium text-slate-600">MVR Expiration</label>
                  <p className="text-slate-900">{formatChicagoDate(driver.mvr_expiration_date)}</p>
                </div>
              )}
              {driver.clearinghouse_expiration_date && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Clearinghouse Expiration</label>
                  <p className="text-slate-900">{formatChicagoDate(driver.clearinghouse_expiration_date)}</p>
                </div>
              )}
              {driver.medical_card_expiration_date && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Medical Expiration</label>
                  <p className="text-slate-900">{formatChicagoDate(driver.medical_card_expiration_date)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Assigned Truck */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 break-inside-avoid mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Assigned Truck
            </h2>
            {truck ? (
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                <Truck className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="font-semibold text-slate-900">Unit #{truck.unit_number}</p>
                  <p className="text-sm text-slate-600">{truck.year} {truck.make} {truck.model}</p>
                  {truck.vin && <p className="text-xs text-slate-500 mt-1">VIN: {truck.vin}</p>}
                </div>
              </div>
            ) : (
              <p className="text-slate-500 italic">No truck assigned</p>
            )}
          </div>

          {/* Assigned Dispatcher */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 break-inside-avoid mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              Assigned Dispatcher
            </h2>
            {dispatcher ? (
              <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
                <UserCheck className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="font-semibold text-slate-900">{dispatcher.full_name || dispatcher.email}</p>
                  <p className="text-sm text-slate-600">{dispatcher.email}</p>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 italic">No dispatcher assigned</p>
            )}
          </div>

          {/* Employment */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 break-inside-avoid mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Employment
            </h2>
            <div className="space-y-3">
              {driver.hire_date && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Hired</label>
                  <p className="text-slate-900">{formatChicagoDate(driver.hire_date)}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-slate-600">Status</label>
                <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                  (driver.driver_status || 'active') === 'active' ? 'bg-green-100 text-green-700' : 
                  (driver.driver_status || 'active') === 'inactive' ? 'bg-slate-100 text-slate-600' :
                  (driver.driver_status || 'active') === 'vacation' ? 'bg-purple-100 text-purple-700' :
                  (driver.driver_status || 'active') === 'on_leave' ? 'bg-yellow-100 text-yellow-700' :
                  (driver.driver_status || 'active') === 'terminated' ? 'bg-red-100 text-red-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {(driver.driver_status || 'active') === 'active' ? 'Active' :
                   (driver.driver_status || 'active') === 'inactive' ? 'Inactive' :
                   (driver.driver_status || 'active') === 'vacation' ? 'Vacation' :
                   (driver.driver_status || 'active') === 'on_leave' ? 'On Leave' :
                   (driver.driver_status || 'active') === 'terminated' ? 'Terminated' :
                   'Active'}
                </span>
              </div>
              {driver.termination_date && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Termination Date</label>
                  <p className="text-slate-900">{formatChicagoDate(driver.termination_date)}</p>
                </div>
              )}
              {driver.termination_letter && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Termination Letter</label>
                  <div className="mt-2">
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <FileText className="w-8 h-8 text-slate-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">
                          {driver.termination_letter.split('/').pop()?.split('?')[0] || 'Termination Letter'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {driver.termination_letter.toLowerCase().endsWith('.pdf') ? 'PDF Document' : 'Document'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <SignedLink
                        stored={driver.termination_letter}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </SignedLink>
                      <button
                        type="button"
                        onClick={() => handleDownloadTerminationLetter()}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {driver.updated_at && (
                <div>
                  <label className="text-sm font-medium text-slate-600">Last Updated</label>
                  <p className="text-slate-900">{formatChicagoDateTime(driver.updated_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* SMS Communication - Removed per user request */}
        </div>
      </div>

      {/* Truck Assignment Modal */}
      {showTruckModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">Assign Truck</h3>
              <button
                onClick={() => setShowTruckModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {availableTrucks.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No available trucks to assign</p>
              ) : (
                <div className="space-y-2">
                  {availableTrucks.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => handleAssignTruck(t.id)}
                      disabled={assigning}
                      className="w-full flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-left disabled:opacity-50"
                    >
                      <Truck className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="font-semibold text-slate-900">Unit #{t.unit_number}</p>
                        <p className="text-sm text-slate-600">{t.year} {t.make} {t.model}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImagePreview && driver.termination_letter && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowImagePreview(false)}>
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowImagePreview(false)}
              className="absolute -top-12 right-0 p-2 bg-white rounded-lg hover:bg-slate-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <SignedImage
              stored={driver.termination_letter}
              alt="Termination Letter Preview"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => handleDownloadTerminationLetter()}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                <Download className="w-5 h-5" />
                Download Image
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispatcher Assignment Modal */}
      {showDispatcherModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">Assign Dispatcher</h3>
              <button
                onClick={() => setShowDispatcherModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {dispatchers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No dispatchers available</p>
              ) : (
                <div className="space-y-2">
                  {dispatchers.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => handleAssignDispatcher(d.id)}
                      disabled={assigning}
                      className="w-full flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors text-left disabled:opacity-50"
                    >
                      <UserCheck className="w-6 h-6 text-purple-600" />
                      <div>
                        <p className="font-semibold text-slate-900">{d.full_name || d.email}</p>
                        <p className="text-sm text-slate-600">{d.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
